/**
 * Minimal IndexedDB persistence (no dependency). Ontologies are stored as their
 * raw text + format and re-parsed on load (an N3 store isn't serializable).
 * Settings and history live in a small key/value store.
 */

const DB_NAME = "nl2sparql";
const DB_VERSION = 1;
const STORE_ONTOLOGIES = "ontologies";
const STORE_KV = "kv";

export interface StoredOntology {
  id: string;
  name: string;
  text: string;
  format: string;
  addedAt: number;
}

export interface StoredEndpoint {
  id: string;
  label: string;
  endpoint: string;
}

export interface HistoryEntry {
  question: string;
  sourceId: string;
  ok: boolean;
  summary: string;
  at: number;
}

export interface Settings {
  anthropicKey?: string;
  endpoints?: StoredEndpoint[];
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_ONTOLOGIES)) db.createObjectStore(STORE_ONTOLOGIES);
      if (!db.objectStoreNames.contains(STORE_KV)) db.createObjectStore(STORE_KV);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode);
        const req = fn(t.objectStore(store));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}

export async function saveOntology(record: StoredOntology): Promise<void> {
  await tx(STORE_ONTOLOGIES, "readwrite", (s) => s.put(record, record.id));
}

export async function listOntologies(): Promise<StoredOntology[]> {
  try {
    return await tx<StoredOntology[]>(STORE_ONTOLOGIES, "readonly", (s) => s.getAll());
  } catch {
    return [];
  }
}

export async function deleteOntology(id: string): Promise<void> {
  await tx(STORE_ONTOLOGIES, "readwrite", (s) => s.delete(id));
}

export async function getKv<T>(key: string): Promise<T | undefined> {
  try {
    return await tx<T | undefined>(STORE_KV, "readonly", (s) => s.get(key));
  } catch {
    return undefined;
  }
}

export async function setKv<T>(key: string, value: T): Promise<void> {
  await tx(STORE_KV, "readwrite", (s) => s.put(value, key));
}

export async function loadSettings(): Promise<Settings> {
  return (await getKv<Settings>("settings")) ?? {};
}

export async function saveSettings(settings: Settings): Promise<void> {
  await setKv("settings", settings);
}

export async function loadHistory(): Promise<HistoryEntry[]> {
  return (await getKv<HistoryEntry[]>("history")) ?? [];
}

export async function saveHistory(entries: HistoryEntry[]): Promise<void> {
  await setKv("history", entries.slice(0, 50));
}
